import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ArrowBack, ArrowForward } from '@material-ui/icons';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { handleErrors, warning } from '../../../_core/services/toast';
import api from '../../../_core/api';
import { BillingInfo, SlipItem } from './types';
import { FullScreenLoading } from '../../../_core/_components/fullscreen-loading';
import * as S from './styles';
import { SaldoAberto } from './abas/saldo-aberto';
import HistoricoNegociacao from './abas/historico-negociacao';
import Inadimplencia from './abas/inadimplencia';

export function BoletosGrupoCobranca(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const month = queryParams.get('month');
  const year = queryParams.get('year');
  const grupoCobranca = queryParams.getAll('grupo-cobranca');
  const portifolio = queryParams.getAll('portifolio');

  const [isLoading, setIsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(1);
  const [boletos, setBoletos] = useState<SlipItem[]>([]);
  const [custumers, setCustumers] = useState<BillingInfo[]>([]);
  const [custumer, setCustumer] = useState<BillingInfo>();

  const getData = async () => {
    try {
      setIsLoading(true);
      const [{ data: details }, { data: boletos }] = await Promise.all([
        api.post('api/allos/v1/prioritization/past-due-balance/billing-group', {
          portifolio,
          month,
          year,
        }),
        api.get(
          `api/allos/v1/prioritization/detail/billinggroup/${grupoCobranca}/month/${month}/year/${year}/slips?page=1&size=9999`,
        ),
      ]);
      const { data } = details;
      setCustumers(data);
      const currentCustumer = data.find((e: any) => e.billingGroup === grupoCobranca[0]);
      setCustumer(currentCustumer);
      setBoletos(boletos);
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    getData();
  }, []);

  const refreshBoletos = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get(
        `api/allos/v1/prioritization/detail/billinggroup/${grupoCobranca}/month/${month}/year/${year}/slips?page=1&size=9999`,
      );
      setBoletos(data);
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  };

  const custumerIndex = useMemo(() => {
    return custumers.findIndex(e => e.billingGroup === custumer?.billingGroup);
  }, [custumer, custumers]);

  const onClickNext = () => {
    if (custumerIndex + 1 === custumers.length) {
      warning('Você já está visualizando o último grupo.');
      return;
    }
    const { billingGroup } = custumers[custumerIndex + 1];
    history.push(
      `/priorizacao-grupo-cobranca?portifolio=${portifolio}&grupo-cobranca=${billingGroup}&month=${month}&year=${year}`,
    );
  };

  const onClickBack = () => {
    if (custumerIndex === 0) {
      warning('Você já está visualizando o primeiro grupo.');
      return;
    }
    const { billingGroup } = custumers[custumerIndex - 1];
    history.push(
      `/priorizacao-grupo-cobranca?portifolio=${portifolio}&grupo-cobranca=${billingGroup}&month=${month}&year=${year}`,
    );
  };

  if (isLoading) return <FullScreenLoading isEnabled />;

  return (
    <S.Content>
      <S.TopActions>
        <S.Back onClick={() => history.push('/priorizacao-grupo-cobranca')}>
          <ArrowBackIcon />
          Retornar para Lista de Priorização
        </S.Back>
        <S.ClientChanger>
          <S.Back onClick={onClickBack}>
            <ArrowBack />
            Cliente Anterior
          </S.Back>

          <S.Back onClick={onClickNext}>
            Próximo Cliente
            <ArrowForward />
          </S.Back>
        </S.ClientChanger>
      </S.TopActions>
      <S.Header>
        <S.HeaderLeft>
          <S.HeaderInfo>
            <S.HeaderInfoLabel>Grupo de Cobrança</S.HeaderInfoLabel>
            <S.HeaderInfoValue>{custumer?.billingGroup}</S.HeaderInfoValue>
          </S.HeaderInfo>
          <S.HeaderInfo>
            <S.HeaderInfoLabel>Portfólio</S.HeaderInfoLabel>
            <S.HeaderInfoValue>{custumer?.portifolio}</S.HeaderInfoValue>
          </S.HeaderInfo>
        </S.HeaderLeft>
        <S.HeaderRight>
          <S.HeaderRightLine>
            <S.HeaderIndicatorWrapper>
              <S.HeaderIndicatorLabel>Vencido mês</S.HeaderIndicatorLabel>
              <S.HeaderIndicatorValue textColor="red">
                {custumer?.overdueThisMonth.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </S.HeaderIndicatorValue>
            </S.HeaderIndicatorWrapper>
            <S.HeaderIndicatorWrapper>
              <S.HeaderIndicatorLabel>Vencido Total (120 dias)</S.HeaderIndicatorLabel>
              <S.HeaderIndicatorValue textColor="black">
                {custumer?.overdue120Days.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </S.HeaderIndicatorValue>
            </S.HeaderIndicatorWrapper>
            <S.HeaderIndicatorWrapper>
              <S.HeaderIndicatorLabel>Vencido Total</S.HeaderIndicatorLabel>
              <S.HeaderIndicatorValue textColor="black">
                {custumer?.totalOverdue.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </S.HeaderIndicatorValue>
            </S.HeaderIndicatorWrapper>
            <S.HeaderIndicatorWrapper>
              <S.HeaderIndicatorLabel>A vencer mês</S.HeaderIndicatorLabel>
              <S.HeaderIndicatorValue textColor="yellow">
                {custumer?.amountDue.toLocaleString('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </S.HeaderIndicatorValue>
            </S.HeaderIndicatorWrapper>
          </S.HeaderRightLine>
        </S.HeaderRight>
      </S.Header>

      <S.TabsWrapper>
        <S.Tabs>
          <S.Tab onClick={() => setCurrentTab(1)} active={currentTab === 1}>
            Saldo em Aberto
          </S.Tab>
          {/* <S.Tab active={currentTab === 2} onClick={() => setCurrentTab(2)}>
            Contatos
          </S.Tab> */}
          <S.Tab active={currentTab === 3} onClick={() => setCurrentTab(3)}>
            Histórico de Negociação
          </S.Tab>
          <S.Tab active={currentTab === 4} onClick={() => setCurrentTab(4)}>
            Inadimplência
          </S.Tab>
        </S.Tabs>
        <S.TabsContent>
          {custumer && currentTab === 1 && (
            <SaldoAberto stateBoletos={[boletos, setBoletos]} getBoletos={refreshBoletos} />
          )}
        </S.TabsContent>
        {/* <S.TabsContent>{custumer && currentTab === 2 && <Contatos contracts={{} as any} />}</S.TabsContent> */}
        <S.TabsContent>
          {custumer && currentTab === 3 && <HistoricoNegociacao customerPriorizationData={custumer} />}
        </S.TabsContent>
        <S.TabsContent>
          {custumer && currentTab === 4 && <Inadimplencia customerPriorizationData={custumer} />}
        </S.TabsContent>
      </S.TabsWrapper>
    </S.Content>
  );
}
